import api from "./api";

// User interface
export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  displayName: string;
  avatar?: string;
  avatarUrl?: string;
  bio?: string;
  location?: string;
  website?: string;
  createdAt: string;
  updatedAt: string;
}

// Login credentials interface
export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegistrationData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  role?: "user" | "artist";
  artistName?: string;
  bio?: string;
  location?: string;
  website?: string;
}

// Reset password data
export interface ResetPasswordData {
  token: string;
  password: string;
  confirmPassword: string;
}

// Auth state interface
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Storage keys
const TOKEN_KEY = "authToken";
const USER_KEY = "authUser";
const TOKEN_EXPIRY_KEY = "authTokenExpires";
const REFRESH_TIMER_KEY = "authRefreshTimer";

// Check if token is expired
const isTokenExpired = (): boolean => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiresAt) return true;

  // Add a 5-minute buffer to prevent edge cases
  const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  return Date.now() > parseInt(expiresAt) - bufferTime;
};

// Get stored auth data
const getStoredAuthData = (): { token: string | null; user: User | null } => {
  const token = localStorage.getItem(TOKEN_KEY);
  const userJson = localStorage.getItem(USER_KEY);

  let user: User | null = null;
  if (userJson) {
    try {
      user = JSON.parse(userJson);
    } catch (e) {
      console.error("Failed to parse stored user data", e);
    }
  }

  return { token, user };
};

// Store auth data
const storeAuthData = (token: string, user: User, expiresIn?: number): void => {
  // If expiresIn is not provided, maintain the existing expiry time from localStorage
  let expiresAt: number;

  if (expiresIn) {
    // New expiry time based on expiresIn (in seconds)
    expiresAt = Date.now() + expiresIn * 1000;
  } else {
    // Use existing expiry time or default to 1 day if none exists
    const existingExpiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
    expiresAt = existingExpiresAt
      ? parseInt(existingExpiresAt)
      : Date.now() + 24 * 60 * 60 * 1000; // Default to 1 day
  }

  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());
  console.log(
    "Auth data stored - Token saved, expires at:",
    new Date(expiresAt).toLocaleString(),
  );
};

// Clear auth data
const clearAuthData = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

// Authentication service
const authService = {
  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const { token } = getStoredAuthData();
    const isTokenValid = !!token && !isTokenExpired();
    console.log(
      "Auth check - Token exists:",
      !!token,
      "Token valid:",
      isTokenValid,
    );

    // Set up automatic token refresh if token exists and is valid
    if (isTokenValid) {
      authService.setupAutoRefresh();
    }

    return isTokenValid;
  },

  // Get current user
  getCurrentUser: (): User | null => {
    const { user } = getStoredAuthData();
    // Add validation to ensure user object is valid
    if (authService.isAuthenticated() && user && user.id && user.username) {
      return user;
    }
    console.warn("User data invalid or incomplete:", user);
    return null;
  },

  // Login user
  login: async (credentials: LoginCredentials): Promise<User> => {
    try {
      const response = await api.post<{
        success: boolean;
        message: string;
        data: { token: string; user: User };
      }>("auth/login", credentials);

      // Extract data from the response
      const { data } = response.data;
      console.log(
        "Login API response structure:",
        JSON.stringify(response.data, null, 2),
      );

      // Check if the data property exists and contains both token and user
      if (!data || !data.token || !data.user) {
        console.error("Login response missing data structure:", response.data);
        throw new Error("Invalid response format from server");
      }

      const { token, user } = data;

      // Validate user object before proceeding
      if (!user || !user.id || !user.username) {
        console.error("Login response missing required user data:", user);
        throw new Error("Invalid user data received from server");
      }

      // Calculate expiresIn (1 day in seconds by default)
      const expiresIn = 24 * 60 * 60; // 1 day in seconds

      console.log(
        "Login successful - Storing auth data, token:",
        token ? "exists" : "missing",
        "expiresIn:",
        expiresIn,
      );
      storeAuthData(token, user, expiresIn);

      // Set a flag to indicate the user just logged in - to prevent redirect loops
      sessionStorage.setItem("justLoggedIn", "true");

      return user;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  },

  // Register new user
  register: async (data: RegistrationData): Promise<User> => {
    try {
      const response = await api.post<{
        success: boolean;
        message: string;
        data: { token: string; user: User };
      }>("auth/register", data);

      // Extract data from the response
      const { data: responseData } = response.data;
      console.log(
        "Register API response structure:",
        JSON.stringify(response.data, null, 2),
      );

      // Check if the data property exists and contains both token and user
      if (!responseData || !responseData.token || !responseData.user) {
        console.error(
          "Register response missing data structure:",
          response.data,
        );
        throw new Error("Invalid response format from server");
      }

      const { token, user } = responseData;

      // Validate user object before proceeding
      if (!user || !user.id || !user.username) {
        console.error(
          "Registration response missing required user data:",
          user,
        );
        throw new Error("Invalid user data received from server");
      }

      // Calculate expiresIn (1 day in seconds by default)
      const expiresIn = 24 * 60 * 60; // 1 day in seconds

      storeAuthData(token, user, expiresIn);
      return user;
    } catch (error) {
      console.error("Registration failed:", error);
      throw error;
    }
  },

  // Logout user
  logout: async (): Promise<void> => {
    try {
      await api.post("auth/logout");
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      clearAuthData();
    }
  },

  // Request password reset
  requestPasswordReset: async (email: string): Promise<void> => {
    await api.post("auth/forgot-password", { email });
  },

  // Reset password
  resetPassword: async (data: ResetPasswordData): Promise<void> => {
    await api.post("auth/reset-password", data);
  },

  // Verify email
  verifyEmail: async (token: string): Promise<void> => {
    await api.post("auth/verify-email", { token });
  },

  // Refresh token
  refreshToken: async (): Promise<boolean> => {
    // Check if there's a token to refresh
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      // No token to refresh, so just return false without making the API call
      return false;
    }

    // If token is expired and we're not in a refresh loop, clear auth data
    // This prevents infinite refresh loops
    if (isTokenExpired()) {
      const refreshAttempts = parseInt(
        localStorage.getItem("refreshAttempts") || "0",
      );
      if (refreshAttempts > 2) {
        // Too many refresh attempts, clear auth data and return false
        clearAuthData();
        localStorage.removeItem("refreshAttempts");
        return false;
      }

      // Increment refresh attempts
      localStorage.setItem("refreshAttempts", (refreshAttempts + 1).toString());
    }

    try {
      const response = await api.post<{
        success: boolean;
        message: string;
        data: { token: string; user: User; expiresIn?: number };
      }>("auth/refresh-token");

      // Extract data from the response
      const { data } = response.data;
      console.log(
        "Refresh token API response structure:",
        JSON.stringify(response.data, null, 2),
      );

      // Check if the data property exists and contains both token and user
      if (!data || !data.token || !data.user) {
        console.error(
          "Refresh token response missing data structure:",
          response.data,
        );
        throw new Error("Invalid response format from server");
      }

      const { token: newToken, user, expiresIn } = data;

      // Validate user object before proceeding
      if (!user || !user.id || !user.username) {
        console.error(
          "Refresh token response missing required user data:",
          user,
        );
        throw new Error("Invalid user data received from server");
      }

      // Reset refresh attempts on success
      localStorage.removeItem("refreshAttempts");

      // Use expiresIn from server if available, otherwise default to 1 day
      const tokenExpiresIn = expiresIn || 24 * 60 * 60; // 1 day in seconds if not specified

      storeAuthData(newToken, user, tokenExpiresIn);

      // Set up automatic refresh
      authService.setupAutoRefresh();

      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      clearAuthData();
      localStorage.removeItem("refreshAttempts");
      throw error; // Throwing error instead of returning false
    }
  },

  // Set up automatic token refresh
  setupAutoRefresh: (): void => {
    // Clear any existing refresh timer
    const existingTimerId = localStorage.getItem(REFRESH_TIMER_KEY);
    if (existingTimerId) {
      clearTimeout(parseInt(existingTimerId));
      localStorage.removeItem(REFRESH_TIMER_KEY);
    }

    // Store the last refresh time
    const lastRefreshTime = Date.now();
    localStorage.setItem("lastTokenRefresh", lastRefreshTime.toString());

    // Get token expiry time
    const expiresAtStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiresAtStr) return;

    const expiresAt = parseInt(expiresAtStr);
    const now = Date.now();

    // If token is already expired, don't set up refresh
    if (now >= expiresAt) return;

    // Calculate time to refresh (75% of time to expiry)
    const timeToExpiry = expiresAt - now;
    const refreshAt = now + timeToExpiry * 0.75;
    const timeUntilRefresh = refreshAt - now;

    console.log(
      "Setting up auto refresh - Current time:",
      new Date(now).toLocaleString(),
      "Expires at:",
      new Date(expiresAt).toLocaleString(),
      "Will refresh at:",
      new Date(refreshAt).toLocaleString(),
      "Time until refresh:",
      Math.round(timeUntilRefresh / 1000),
      "seconds",
    );

    // Set up timer to refresh token
    const timerId = setTimeout(async () => {
      console.log("Auto-refreshing token at:", new Date().toLocaleString());
      await authService.refreshToken();
    }, timeUntilRefresh);

    // Store timer ID in localStorage to track across page reloads
    localStorage.setItem(REFRESH_TIMER_KEY, timerId.toString());

    // Set up token refresh on page load/visibility change for better persistence
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const lastRefresh = parseInt(
          localStorage.getItem("lastTokenRefresh") || "0",
        );
        const now = Date.now();
        // If it's been more than 30 minutes since last refresh, do it again
        if (now - lastRefresh > 30 * 60 * 1000) {
          console.log("Page became visible, refreshing token after inactivity");
          authService.refreshToken();
        }
      }
    };

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Also refresh on page load if needed
    if (document.readyState === "complete") {
      handleVisibilityChange();
    } else {
      window.addEventListener("load", handleVisibilityChange);
    }
  },

  // Update user profile
  updateProfile: async (userData: Partial<User>): Promise<User> => {
    try {
      const response = await api.put<{
        success: boolean;
        message: string;
        data: { user: User };
      }>("/users/profile", userData);

      // Extract data from the response
      const { data } = response.data;
      console.log(
        "Update profile API response structure:",
        JSON.stringify(response.data, null, 2),
      );

      // Check if the data property exists and contains user
      if (!data || !data.user) {
        console.error(
          "Update profile response missing data structure:",
          response.data,
        );
        throw new Error("Invalid response format from server");
      }

      const { user } = data;

      // Validate user object before proceeding
      if (!user || !user.id || !user.username) {
        console.error(
          "Update profile response missing required user data:",
          user,
        );
        throw new Error("Invalid user data received from server");
      }

      // Update stored user data
      const { token } = getStoredAuthData();
      if (token) {
        // Use default 1 day expiration
        const expiresIn = 24 * 60 * 60; // 1 day in seconds
        storeAuthData(token, user, expiresIn);
      }

      return user;
    } catch (error) {
      console.error("Update profile failed:", error);
      throw error;
    }
  },
};

// Setup auto refresh on module load if user is authenticated
if (authService.isAuthenticated()) {
  authService.setupAutoRefresh();
}

export default authService;
